import React, { useCallback, useEffect, useRef } from 'react';
import { ApolloError, NetworkStatus } from '@apollo/client';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import GettextFactory from '../../../util/GettextFactory';
import { useSqPrefillPreference } from '../hooks/useSqPrefillPreference';
import {
    useGetAutoApplyEligiblePastAnswersQuery,
    useGetSqPrefillPreferenceQuery,
} from './__generated__/PastAnswers.generated';
import {
    PAST_ANSWERS_PAGE_SIZE,
    PastAnswersPageContent,
    type PastAnswersPageConnection,
} from './PastAnswersPageContent';
import {
    type AutoApplySetupRumErrorContext,
    AutoApplySetupRumContext as RumContext,
    logAutoApplySetupRumError,
} from '../autoApplySetupRumErrors';

export const useShouldIncludeHostedSQ = (): boolean => {
    const { getGroup } = useProctorGroups();
    const value = getGroup('ia_afm_iqv2_prefill_tst')?.value;
    return value === 1 || value === 2;
};

/** SQ-specific queries and preference mutation for the shared Past Answers UI. */
export const SqPastAnswersPage: React.FC = () => {
    const gt = GettextFactory();
    const shouldIncludeHostedSQ = useShouldIncludeHostedSQ();
    const reportedErrors = useRef<{
        pastAnswers?: Error;
        prefill?: Error;
        mutation?: Error;
    }>({});
    const {
        data: prefillData,
        error: prefillError,
        refetch: refetchPrefill,
    } = useGetSqPrefillPreferenceQuery({ fetchPolicy: 'cache-first' });
    const {
        usePastAnswers,
        handleUsePastAnswersChange,
        error: mutationError,
        isVerificationError,
        saving,
    } = useSqPrefillPreference(prefillData?.autoApplyInfo?.sqPastAnswerPrefillPreference);
    const {
        data,
        loading,
        networkStatus,
        error: pastAnswersError,
        refetch: refetchPastAnswers,
        fetchMore,
    } = useGetAutoApplyEligiblePastAnswersQuery({
        variables: { first: PAST_ANSWERS_PAGE_SIZE, shouldIncludeHostedSQ },
        notifyOnNetworkStatusChange: true,
        ssr: false,
    });

    useEffect(() => {
        const reportOnce = (
            key: keyof typeof reportedErrors.current,
            error: Error | undefined,
            errorContext: AutoApplySetupRumErrorContext,
            isPageFailure?: boolean,
        ): void => {
            if (!error) {
                delete reportedErrors.current[key];
            } else if (reportedErrors.current[key] !== error) {
                reportedErrors.current[key] = error;
                logAutoApplySetupRumError(error, errorContext, isPageFailure);
            }
        };

        reportOnce('pastAnswers', pastAnswersError, RumContext.PastAnswersLoad);
        reportOnce('prefill', prefillError, RumContext.PastAnswersLoad);
        reportOnce('mutation', mutationError, RumContext.PastAnswersAction, isVerificationError);
    }, [pastAnswersError, prefillError, mutationError, isVerificationError]);

    const apiConnection = data?.autoApplyEligiblePastAnswers;
    const connection: PastAnswersPageConnection | undefined = apiConnection
        ? {
              edges: apiConnection.edges.map((edge) => edge.node),
              pageInfo: apiConnection.pageInfo,
              totalCount: apiConnection.totalCount,
          }
        : undefined;

    const onFetchMore = useCallback(
        (first: number, after: string) =>
            fetchMore({ variables: { first, after, shouldIncludeHostedSQ } })
                .then((result) => {
                    // errorPolicy: 'all' resolves GraphQL failures instead of rejecting.
                    if (result.errors?.length) {
                        throw new ApolloError({ graphQLErrors: result.errors });
                    }
                    return result;
                })
                .catch((error: unknown) => {
                    logAutoApplySetupRumError(error, RumContext.PastAnswersLoadMore, false);
                    throw error;
                }),
        [fetchMore, shouldIncludeHostedSQ],
    );
    const onRetrySource = useCallback(() => {
        if (pastAnswersError) void refetchPastAnswers();
        if (prefillError) void refetchPrefill();
        if (mutationError) void handleUsePastAnswersChange(!usePastAnswers);
    }, [
        pastAnswersError,
        prefillError,
        mutationError,
        refetchPastAnswers,
        refetchPrefill,
        handleUsePastAnswersChange,
        usePastAnswers,
    ]);

    return (
        <PastAnswersPageContent
            connection={connection}
            usePastAnswers={usePastAnswers}
            onUsePastAnswersChange={handleUsePastAnswersChange}
            toggleDisabled={saving}
            headerTitle={gt.gettext('Application questions')}
            toggleLabel={gt.gettext('Autofill application questions')}
            fetching={loading || networkStatus === NetworkStatus.fetchMore}
            sourceError={Boolean(pastAnswersError || prefillError || mutationError)}
            onRetrySource={onRetrySource}
            onFetchMore={onFetchMore}
        />
    );
};
